import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { LocalStorageKeyOptions } from 'utils/constants/storage';

import { storage } from 'utils/web-storage';

function useLocalStorageSubscribe(callback: () => void) {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
}

const getLocalStorageServerSnapshot = () => {
  // throw Error('useLocalStorage is a client-only hook');
  return null;
};

export function useLocalStorage<T>(key: LocalStorageKeyOptions, initialValue: T) {
  const getSnapshot = () => storage.local.get(key, { parse: false });

  const store = useSyncExternalStore(useLocalStorageSubscribe, getSnapshot, getLocalStorageServerSnapshot);

  const setState = useCallback(
    (v: T) => {
      try {
        const nextState = typeof v === 'function' ? v(JSON.parse(store as string)) : v;

        if (nextState === undefined || nextState === null) {
          storage.local.remove(key);
        } else {
          storage.local.set(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store],
  );

  useEffect(() => {
    if (storage.local.get(key) === null && typeof initialValue !== 'undefined') {
      storage.local.set(key, initialValue);
    }
  }, [key, initialValue]);

  let parsedValue = initialValue;
  try {
    if (store) {
      parsedValue = JSON.parse(store as string) as T;
    }
  } catch (error) {
    console.warn(error);
  }

  return [parsedValue, setState] as const;
}
